/**
 * Advanced mode's own groups (2026-09-29).
 *
 * Advanced used to be iJewel Playground V2's panel, tab for tab: "Picking"
 * held materials AND the transform gizmo, "Scene" held lighting, the ground and
 * two legacy duplicates, "Post Processing" held eighteen effects in capture
 * order. The user asked for it to be reorganised into groups of our own, by
 * what a pro is doing — so the sections are REGROUPED here, and nothing else
 * about them changes:
 *
 *   - every section keeps its iJewel `title` verbatim (verify:parity still
 *     matches all 69); what is SHOWN is the plain title below, and search
 *     finds both;
 *   - the section modules (viewer.ts, picking.ts, …) keep their own order and
 *     exports — they are the parts bin, this file is the shelf;
 *   - experimental / debug / duplicate sections stay in their group but are
 *     only shown when "Show experimental" is on (a switch in the panel header).
 */

export type GroupId =
  | "materials"
  | "lighting"
  | "ground"
  | "camera"
  | "effects"
  | "quality"
  | "output"
  | "animate"
  | "configure"
  | "tools";

export interface GroupDef {
  id: GroupId;
  label: string;
  icon: string;
  /** Section ids, in display order. */
  sections: string[];
}

export const GROUPS: GroupDef[] = [
  {
    id: "materials",
    label: "Materials",
    icon: "texture",
    sections: [
      "hierarchy",
      "materialLibrary",
      "material",
      "diamondRayTracing",
      "materialAdvanced",
      "materialMaps",
      "materialExtensions",
      "diamonds",
      "savedMaterials",
    ],
  },
  {
    id: "lighting",
    label: "Lighting",
    icon: "light_mode",
    sections: [
      "environment",
      "studioPresets",
      "scenePresets",
      "savedEnvironments",
      "overallLighting",
      "keyLight",
      "fillLight",
      "hemiLight",
      "sceneLights",
      "metalEnv",
      "bgEnvAdvanced",
      "gradientSvg",
    ],
  },
  {
    id: "ground",
    label: "Ground",
    icon: "ev_shadow",
    sections: [
      "groundFull",
      "groundShadowBake",
      "ground",
      "sscs",
      "randomizedSelfShadow",
      "hdriGround",
      "progressiveShadow",
    ],
  },
  {
    id: "camera",
    label: "Camera",
    icon: "photo_camera",
    sections: ["camera", "camera-views", "dof", "virtualCameras", "interaction-prompt", "parallaxCamera", "scrollCamera"],
  },
  {
    id: "effects",
    label: "Effects",
    icon: "auto_fix_high",
    sections: [
      "postMaster",
      "tonemapping",
      "bloom",
      "ssr",
      "ssao",
      "gemRefraction",
      "vignette",
      "chromatic",
      "grain",
      "lut",
      "outline",
      "ssbevel",
      "reflectionProbe",
      "reflectionMatrix",
      "ssgi",
      "debug",
    ],
  },
  {
    id: "quality",
    label: "Quality",
    icon: "high_quality",
    sections: ["renderer", "renderQuality", "progressive", "taa", "motion", "velocity"],
  },
  {
    id: "output",
    label: "Output",
    icon: "ios_share",
    sections: [
      "imageExport",
      "video",
      "assetExport",
      "shareLink",
      "branding",
      "loadingScreen",
      "frameFade",
      "fullScreen",
      "sceneSettings",
      "metalMaterial",
      "diamondMaterial",
      "s3",
    ],
  },
  {
    id: "animate",
    label: "Animate",
    icon: "play_circle",
    sections: ["gltf-clips", "timeline", "posePlugin", "sceneLoop", "physics"],
  },
  {
    id: "configure",
    label: "Configure",
    icon: "tune",
    sections: ["variationConfigurator", "materialConfigurator", "switchNode", "khr"],
  },
  {
    id: "tools",
    label: "Tools",
    icon: "handyman",
    sections: [
      "transform",
      "pivotControls",
      "pivotEdit",
      "nodeInspector",
      "nodeGeometry",
      "picker",
      "widgets",
      "drop-options",
      "rhinoLoader",
      "generateGeometry",
      "objectRotations",
      "extrudeTube",
      "simpleText",
      "simplify",
      "parallaxMapping",
      "modelStage",
      "watchHands",
      "css3d",
      "snowFall",
      "waveGround",
      "rainbowDiamond",
      "uvUnwrap",
      "materialExtensionToggles",
      "extras",
    ],
  },
];

/**
 * What each section is CALLED on screen — plain, sentence case. The iJewel
 * name stays the section's `title` (parity) and search matches it too, so an
 * iJewel-trained artist typing "SSBevel" still lands on "Edge bevel".
 */
export const PLAIN_TITLES: Record<string, string> = {
  // Materials
  hierarchy: "Parts",
  materialLibrary: "Material library",
  material: "Metal (selected part)",
  diamondRayTracing: "Diamond & gem (selected part)",
  materialAdvanced: "Physical material",
  materialMaps: "Texture maps",
  materialExtensions: "Material extensions",
  diamonds: "Diamond setup & gem lighting",
  savedMaterials: "Saved materials",
  // Lighting
  environment: "Background & environment",
  studioPresets: "Studio presets",
  scenePresets: "Scene presets",
  savedEnvironments: "Saved environments",
  overallLighting: "Overall light",
  keyLight: "Key light",
  fillLight: "Fill light",
  hemiLight: "Ambient light",
  sceneLights: "Extra lights",
  metalEnv: "Metal reflections map",
  bgEnvAdvanced: "Second environment",
  gradientSvg: "Gradient background",
  // Ground
  groundFull: "Ground",
  groundShadowBake: "Ground shadow",
  ground: "Floor reflection",
  sscs: "Contact shadows",
  randomizedSelfShadow: "Self-shadow",
  hdriGround: "Environment floor",
  progressiveShadow: "Progressive shadow",
  // Camera
  camera: "Camera",
  "camera-views": "Saved views",
  dof: "Depth of field",
  virtualCameras: "Virtual cameras",
  "interaction-prompt": "Interaction hint",
  parallaxCamera: "Parallax camera",
  scrollCamera: "Scroll camera preview",
  // Effects
  postMaster: "All effects",
  tonemapping: "Colour & tone mapping",
  bloom: "Glow (bloom)",
  ssr: "Reflections (SSR)",
  ssao: "Ambient occlusion (SSAO)",
  gemRefraction: "Gem refraction",
  vignette: "Vignette",
  chromatic: "Chromatic aberration",
  grain: "Film grain",
  lut: "Colour lookup (LUT)",
  outline: "Selection outline",
  ssbevel: "Edge bevel",
  reflectionProbe: "Reflection probe",
  reflectionMatrix: "Reflection matrix",
  ssgi: "Global illumination (SSGI)",
  debug: "Pipeline debug",
  // Quality
  renderer: "Resolution",
  renderQuality: "Render quality",
  progressive: "Progressive refinement",
  taa: "Anti-aliasing (TAA)",
  motion: "Motion quality",
  velocity: "Motion vectors",
  // Output
  imageExport: "Image export",
  video: "Video export",
  assetExport: "3D file export",
  shareLink: "Share link",
  branding: "Branding",
  loadingScreen: "Loading screen",
  frameFade: "Frame fade",
  fullScreen: "Full screen",
  sceneSettings: "Scene settings file",
  metalMaterial: "Metal material file",
  diamondMaterial: "Diamond material file",
  s3: "Cloud storage (S3)",
  // Animate
  "gltf-clips": "Model animations",
  timeline: "Keyframe timeline",
  posePlugin: "Pose",
  sceneLoop: "Scene loop (kiosk)",
  physics: "Physics",
  // Configure
  variationConfigurator: "Variations",
  materialConfigurator: "Material configurator",
  switchNode: "Switchable parts",
  khr: "Material variants (glTF)",
  // Tools
  transform: "Move, rotate, scale",
  pivotControls: "Pivot",
  pivotEdit: "Edit pivot",
  nodeInspector: "Selected object",
  nodeGeometry: "Geometry",
  picker: "Picking",
  widgets: "Helpers",
  "drop-options": "Import options",
  rhinoLoader: "Rhino import",
  generateGeometry: "Generate geometry",
  objectRotations: "Radial copies",
  extrudeTube: "Extrude tube",
  simpleText: "3D text",
  simplify: "Simplify mesh",
  parallaxMapping: "Parallax mapping",
  modelStage: "Display stand",
  watchHands: "Watch hands",
  css3d: "HTML overlay",
  snowFall: "Snow",
  waveGround: "Wave floor",
  rainbowDiamond: "Rainbow diamond",
  uvUnwrap: "UV unwrap",
  materialExtensionToggles: "Material extension toggles",
  extras: "Extras",
};

/**
 * Sections shown only with "Show experimental" on, and why. Nothing here is
 * deleted — a developer or an iJewel-parity check still reaches every one.
 */
export const EXPERIMENTAL: Record<string, string> = {
  ssgi: "Developer preview of screen-space global illumination.",
  debug: "Render-pipeline debug views for developers.",
  scrollCamera: "Developer preview of scroll-driven camera paths.",
  extras: "Advanced and debug switches.",
  uvUnwrap: "Best-effort: a box-projection unwrap, not a true UV unwrap.",
  progressiveShadow: "A second copy of the ground shadow controls in Ground.",
  materialExtensionToggles: "Stored with the file but has no effect on the render.",
  s3: "Kept for iJewel parity; your Vault stores files already.",
};

/** Map the retired iJewel tab ids to the group a saved UI state should reopen. */
export const OLD_TAB_TO_GROUP: Record<string, GroupId> = {
  viewer: "camera",
  picking: "materials",
  scene: "lighting",
  aa: "quality",
  postfx: "effects",
  export: "output",
  animations: "animate",
  modifiers: "tools",
  configurators: "configure",
  plugins: "tools",
  editor: "materials",
};
